import { can } from '@pidelo/contracts';
import { isApiErrorCode, normalizeSearch, useAdminSession, useApiGet, useLiveEvents } from '@pidelo/web-core';
import { AppShell, Banner, Button, LiveIndicator, Logo, LogoMark, PrivateSearch, SearchInput, type ShellNavGroup } from '@pidelo/web-ui';
import { useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';
import { session, useShiftNotice } from '../app/core.ts';
import { SECTIONS, SECTION_GROUPS, menuLabel, sectionHref, sectionOfPath } from '../app/sections.ts';
import { useLocalMenuList, useWaitingChats } from '../sections/local-menu/local-menu-data.ts';
import { AccountSheet } from './AccountSheet.tsx';
import { ShiftPanel } from './ShiftPanel.tsx';
import { useShiftControls } from './use-shift-controls.ts';
import { useAdminAccess } from './use-admin-access.ts';
import styles from './AdminLayout.module.css';

/** The admin frame: menu by permissions, shift, live connection and the account sheet. */
export function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const access = useAdminAccess();
  const sessionQuery = useAdminSession(session);
  const live = useLiveEvents('/admin/events');
  const notice = useShiftNotice();
  const [accountOpen, setAccountOpen] = useState(false);
  const [menuSearch, setMenuSearch] = useState('');
  useEffect(() => setMenuSearch(''), [location.pathname]);
  // The counters of La carta del local: changes to approve, QR to deliver and businesses waiting for an answer (A333, A334).
  const seesBusinesses = can(access, 'BUSINESSES_VIEW');
  const localMenu = useLocalMenuList(seesBusinesses);
  const waitingChats = useWaitingChats(seesBusinesses);
  const waitingSupport = useApiGet<{ waitingCount: number }, number>(['growth', 'support-waiting'], '/admin/support/threads', { query: { filter: 'waiting', limit: 1 }, enabled: can(access, 'CLAIMS_VIEW'), select: data => data.waitingCount, refetchInterval: 30_000 });
  const pendingChanges = localMenu.data?.pendingChanges ?? 0;
  const pendingQr = localMenu.data?.pendingQrOrders ?? 0;
  const waiting = (waitingChats.data ?? 0) + (waitingSupport.data ?? 0);

  const nav = useMemo<ShellNavGroup[]>(
    () =>
      SECTION_GROUPS.map(group => ({
        label: group.label,
        items: group.ids
          .map(id => SECTIONS[id])
          .filter(section => section.allowed(access))
          .filter(section => !menuSearch.trim() || normalizeSearch(`${group.label} ${section.title} ${menuLabel(section)} ${section.description}`).includes(normalizeSearch(menuSearch.trim())))
          .map(section => {
            const Icon = section.icon;
            const badge = { menuChanges: pendingChanges, localMenu: pendingQr, chats: waiting }[section.id as string] ?? 0;
            return { to: sectionHref(section), label: menuLabel(section), icon: <Icon />, badge: badge > 0 ? badge : undefined, end: section.path === '' || sectionOfPath(location.pathname)?.id !== section.id };
          }),
      })).filter(group => group.items.length > 0),
    [access, location.pathname, menuSearch, pendingChanges, pendingQr, waiting],
  );

  const shift = useShiftControls();

  const section = sectionOfPath(location.pathname);
  const onShift = shift?.shift !== null;
  const sessionError = sessionQuery.error && !isApiErrorCode(sessionQuery.error, 'unauthorized') ? sessionQuery.error : null;

  return (
    <>
      <AppShell
        brand={
          <span className={styles.brand}>
            <Logo surface="dark" height={30} />
          </span>
        }
        brandCompact={<LogoMark variant="white" size={34} />}
        nav={nav}
        navSearch={<><SearchInput value={menuSearch} onChange={setMenuSearch} aria-label="Buscar apartado" placeholder="Buscar apartado" />{nav.length === 0 ? <p className={styles.noResults}>No hay apartados con ese nombre.</p> : null}</>}
        currentPath={location.pathname}
        LinkComponent={Link}
        title={section ? menuLabel(section) : 'Pídelo'}
        topBarStatus={
          <span className={styles.chips}>
            <LiveIndicator status={live.status} surface="dark" compact />
            {shift ? <span className={styles.shiftChip} data-on={onShift ? '' : undefined}>{onShift ? 'De turno' : 'Fuera de turno'}</span> : null}
          </span>
        }
        sidebarExtra={({ collapsed, phone }) => (
          <>
            {shift ? <ShiftPanel controls={shift} collapsed={collapsed} /> : null}
            {phone ? null : <LiveIndicator status={live.status} surface="dark" compact={collapsed} className={styles.live} />}
          </>
        )}
        user={{ name: access.name || 'Tu cuenta', detail: access.isOwner ? 'Principal' : 'Equipo', onClick: () => setAccountOpen(true) }}
        banner={
          <>
            {notice.visible && shift && !onShift ? (
              <Banner
                tone="warn"
                title="Comienza tu turno para seguir"
                onDismiss={notice.hide}
                action={
                  <Button size="sm" onClick={shift.start} loading={shift.pending}>
                    Comenzar mi turno
                  </Button>
                }
              >
                Para verificar pagos y responder reclamos tienes que estar de turno.
              </Banner>
            ) : null}
            {sessionError ? <Banner tone="error">No pudimos revisar tus permisos. Lo intentamos de nuevo en cuanto haya conexión.</Banner> : null}
          </>
        }
      >
        <div className={styles.privateSearch}><PrivateSearch surface="admin" identity={`${access.userId}:${access.isOwner}:${access.permissions.join(',')}`} onNavigate={navigate} /></div>
        <Outlet />
      </AppShell>
      <AccountSheet open={accountOpen} onClose={() => setAccountOpen(false)} shift={shift} />
    </>
  );
}
